import type { ILocaleConfig } from "@/types/localeTypes";
import { appConfigService } from "@/services/appConfigService";
import { authService } from "@/services/authService";

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1000;

async function getAuthHeaders(): Promise<HeadersInit> {
  await authService.createAuthProvider();
  const token = await authService.getAccessToken();

  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  return headers;
}

export async function fetchAvailableLocales(): Promise<ILocaleConfig[]> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const config = await appConfigService.getAppEnvironmentData();
      const headers = await getAuthHeaders();

      const response = await fetch(`${config.apiBaseUrl}/languages`, {
        method: "GET",
        headers,
        credentials: "include",
      });

      if (response.status === 503 && attempt < MAX_RETRIES) {
        await new Promise<void>((resolve): void => {
          setTimeout(resolve, RETRY_DELAY_MS);
        });
        continue;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch locales: ${response.status} ${response.statusText}`);
      }

      const data = (await response.json()) as Array<{
        isoCode: string;
        cultureName: string;
        isDefault: boolean;
        isMandatory: boolean;
        fallbackIsoCode?: string;
      }>;

      return data.map((lang) => ({
        code: lang.isoCode,
        name: lang.cultureName,
        isEnabled: true,
        isDefault: lang.isDefault,
      }));
    } catch (error) {
      lastError = error instanceof Error ? error : new Error("Unknown error occurred");

      if (attempt < MAX_RETRIES) {
        await new Promise<void>((resolve): void => {
          setTimeout(resolve, RETRY_DELAY_MS);
        });
      }
    }
  }

  throw lastError || new Error("Failed to fetch locales after retries");
}
